import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw } from 'lucide-react';
import {API_BASE, getToken} from '../../lib/api';
const API_URL = API_BASE;


const fmt = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getFYStart = () => {
  const d = new Date(); const y = d.getFullYear(); const m = d.getMonth() + 1;
  return m >= 4 ? `${y}-04-01` : `${y - 1}-04-01`;
};

/**
 * GLLedgerModal — Full GL Ledger popup with date range + running balance.
 * Opens from VoucherForm line items ⋮ → "Open Ledger"
 * Zero navigation — stays within the voucher form.
 */
const GLLedgerModal = ({ accountId, accountName, onClose }) => {
  const [fromDate, setFromDate] = useState(getFYStart);
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true); setError('');
    try {
      const tok = getToken();
      const url = `${API_URL}/api/finance/account-history?accountId=${encodeURIComponent(accountId)}&limit=500&fromDate=${fromDate}&toDate=${toDate}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
      const json = await res.json();
      if (json.success) {
        // API returns DESC order — reverse to ASC for running balance
        const asc = [...(json.data || [])].reverse();
        // Compute running balance (Dr = positive, Cr = negative)
        let balance = 0;
        const withBal = asc.map(r => {
          const dr = parseFloat(r.DrAmount) || 0;
          const cr = parseFloat(r.CrAmount) || 0;
          balance += dr - cr;
          return { ...r, runningBalance: balance };
        });
        setRows(withBal.reverse()); // Back to DESC for display
      } else {
        setError(json.error || 'Failed to load ledger');
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    }
    setLoading(false);
  }, [accountId, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const totalDr = rows.reduce((s, r) => s + (parseFloat(r.DrAmount) || 0), 0);
  const totalCr = rows.reduce((s, r) => s + (parseFloat(r.CrAmount) || 0), 0);
  const netBalance = totalDr - totalCr;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="gl-ledger-modal"
    >
      <div style={{ background: 'var(--erp-surface)', border: '1px solid var(--erp-border)', borderRadius: 8, width: 'min(96vw, 900px)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--erp-border)', background: 'var(--erp-primary)', color: '#fff', borderRadius: '8px 8px 0 0', flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>GL Ledger</div>
            <div style={{ fontSize: 14, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-testid="gl-modal-account-name">{accountName || accountId}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 4, cursor: 'pointer', padding: '4px 8px' }} data-testid="gl-modal-close"><X size={14} /></button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--erp-border)', flexShrink: 0, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, color: 'var(--erp-text-muted)', fontWeight: 600 }}>From</span>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="erp-input" style={{ height: 26, fontSize: 10, width: 120 }} data-testid="gl-modal-from-date" />
          <span style={{ fontSize: 10, color: 'var(--erp-text-muted)', fontWeight: 600 }}>To</span>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="erp-input" style={{ height: 26, fontSize: 10, width: 120 }} data-testid="gl-modal-to-date" />
          <button onClick={load} disabled={loading} className="erp-btn erp-btn-ghost" style={{ height: 26, fontSize: 10, padding: '0 10px' }} data-testid="gl-modal-refresh-btn">
            <RefreshCw size={11} style={{ marginRight: 4, animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--erp-text-muted)' }}>{rows.length} transactions</span>
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {error ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--erp-danger)', fontSize: 12 }}>{error}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--erp-surface)', zIndex: 1 }}>
                <tr style={{ borderBottom: '2px solid var(--erp-border)' }}>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, width: 95 }}>Date</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700 }}>Voucher / Narration</th>
                  <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, width: 110 }}>Dr Amt</th>
                  <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, width: 110 }}>Cr Amt</th>
                  <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, width: 120 }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--erp-text-muted)' }}>Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--erp-text-muted)' }}>No transactions found for this date range.</td></tr>
                )}
                {rows.map((r, i) => {
                  const dr = parseFloat(r.DrAmount) || 0;
                  const cr = parseFloat(r.CrAmount) || 0;
                  const bal = r.runningBalance;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--erp-border)', background: i % 2 === 0 ? 'transparent' : 'var(--erp-bg,rgba(0,0,0,0.02))' }}>
                      <td style={{ padding: '5px 10px', whiteSpace: 'nowrap', color: 'var(--erp-text-muted)' }}>{r.DocDate}</td>
                      <td style={{ padding: '5px 10px' }}>
                        <div style={{ fontWeight: 600 }}>{r.DocId}</div>
                        <div style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>{r.VoucherType}{r.Narration ? ` — ${r.Narration}` : ''}</div>
                      </td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--erp-font-mono)', color: dr > 0 ? '#16a34a' : 'var(--erp-text-muted)' }}>{dr > 0 ? fmt(dr) : '—'}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--erp-font-mono)', color: cr > 0 ? '#dc2626' : 'var(--erp-text-muted)' }}>{cr > 0 ? fmt(cr) : '—'}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontWeight: 600, color: bal >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(Math.abs(bal))} {bal >= 0 ? 'Dr' : 'Cr'}</td>
                    </tr>
                  );
                })}
              </tbody>
              {rows.length > 0 && (
                <tfoot style={{ borderTop: '2px solid var(--erp-border)', background: 'var(--erp-surface)', position: 'sticky', bottom: 0 }}>
                  <tr>
                    <td colSpan={2} style={{ padding: '6px 10px', fontWeight: 700, fontSize: 11 }}>TOTAL ({rows.length} transactions)</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontWeight: 700, color: '#16a34a' }}>{fmt(totalDr)}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontWeight: 700, color: '#dc2626' }}>{fmt(totalCr)}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontWeight: 700, color: netBalance >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(Math.abs(netBalance))} {netBalance >= 0 ? 'Dr' : 'Cr'}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '6px 16px', borderTop: '1px solid var(--erp-border)', display: 'flex', alignItems: 'center', gap: 16, fontSize: 10, color: 'var(--erp-text-muted)', flexShrink: 0 }}>
          <span>Account: <strong style={{ color: 'var(--erp-text)' }}>{accountId}</strong></span>
          <span>Dr: <strong style={{ color: '#16a34a', fontFamily: 'var(--erp-font-mono)' }}>{fmt(totalDr)}</strong></span>
          <span>Cr: <strong style={{ color: '#dc2626', fontFamily: 'var(--erp-font-mono)' }}>{fmt(totalCr)}</strong></span>
          <span>Net: <strong style={{ fontFamily: 'var(--erp-font-mono)' }}>{fmt(Math.abs(netBalance))} {netBalance >= 0 ? 'Dr' : 'Cr'}</strong></span>
          <button onClick={onClose} className="erp-btn erp-btn-ghost" style={{ marginLeft: 'auto', height: 26, fontSize: 10, padding: '0 12px' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default GLLedgerModal;
